'use client';

// imports
import Cookies from 'js-cookie';
import { useAtom } from "jotai";
import { useState, useEffect } from "react";

// lib files
import { sendPbsGtmEvent } from "@/lib/helpers/send-pbs-gtm-event";
import { stationConfirmedAtom } from '@/lib/atoms/station-confirmed';
import { StationData } from "@/lib/types/api/stations-data";
import { useHasMounted } from "@/lib/hooks";

// components
import LinkButton from '@/components/Button/LinkButton';

// SVG's
import CloseIcon from '/public/svg/close.svg';
import HeartIcon from '/public/svg/donate-heart.svg';

// styles
import styles from './UrgentMessagePopover.module.scss';
interface UrgentMessagePopoverProps {
  headline?: string;
  stationData: StationData;
  text?: string;
  depIsOpen?: boolean;
}
const HIDE_POPOVER_COOKIE = 'hideUrgentMessagePopover';
const defaultHeadline = 'Protect PBS';
const defaultText = 'Defunded but not defeated. Your support is essential. Help keep your local PBS station strong in their mission to inspire, enrich, and educate.';
const UrgentMessagePopover = (props: UrgentMessagePopoverProps) => {
  const {
    headline = defaultHeadline,
    text = defaultText,
    stationData,
    depIsOpen
  } = props;
  const {
    attributes: {
      donate_url,
      short_common_name
    }
  } = stationData;
  const hasMounted = useHasMounted();
  const [stationConfirmed, _] = useAtom(stationConfirmedAtom);
  const [hidden, setHidden] = useState(false);
  const isHiddenCookieSet = depIsOpen !== undefined ? !depIsOpen : Cookies.get(HIDE_POPOVER_COOKIE) === 'true';
  useEffect(() => {
    if (hasMounted && !isHiddenCookieSet && stationConfirmed) {
      sendPbsGtmEvent({
        feature_category: "urgent messaging",
        feature_name: "popover",
        object_name: "popover",
        object_action: "impression",
        object_action_behavior: "display content"
      });
    }
  }, [hasMounted, isHiddenCookieSet, stationConfirmed]);

  // Checks if the component is mounted on the client
  // or if it's hidden
  if (!hasMounted || isHiddenCookieSet || depIsOpen === undefined && !stationConfirmed) {
    return null;
  }
  const hidePopover = () => {
    // Set the cookie to hide the popover
    // after half a second in order to allow
    // the fade out animation.
    setTimeout(() => {
      Cookies.set(HIDE_POPOVER_COOKIE, 'true', {
        domain: '.pbs.org',
        path: '/',
        expires: 30,
        sameSite: 'none',
        secure: true
      });
    }, 500);
    setHidden(true);
    sendPbsGtmEvent({
      feature_category: "urgent messaging",
      feature_name: "popover",
      object_name: "close button",
      object_type: "button",
      object_action: "click",
      object_action_behavior: "close urgent message"
    });
  };
  return <div className={styles.urgent_message_popover} hidden={hidden} data-sentry-component="UrgentMessagePopover" data-sentry-source-file="UrgentMessagePopover.tsx">
      <button className={styles.urgent_message_popover_close_button} onClick={hidePopover} aria-label='Close'>
        <CloseIcon data-sentry-element="CloseIcon" data-sentry-source-file="UrgentMessagePopover.tsx" />
      </button>

      <div className={styles.urgent_message_popover_content}>
        <h2 className={styles.urgent_message_popover_headline}>{headline}</h2>
        <p className={styles.urgent_message_popover_text}>{text}</p>

        <LinkButton href={donate_url || 'https://www.pbs.org/donate/'} target="_blank" style="white" className={styles.urgent_message_popover_donate_button} onClick={() => {
        sendPbsGtmEvent({
          feature_category: "urgent messaging",
          feature_name: "popover",
          object_name: "donate button",
          object_type: "button",
          object_action: "click",
          object_action_behavior: "navigate to donation page"
        });
      }} data-sentry-element="LinkButton" data-sentry-source-file="UrgentMessagePopover.tsx">
          <HeartIcon className={styles.urgent_message_popover_donate_button_heart_icon} data-sentry-element="HeartIcon" data-sentry-source-file="UrgentMessagePopover.tsx" />
          Donate
          <span className={styles.urgent_message_popover_donate_button_station}>
            to {short_common_name}
          </span>
        </LinkButton>
      </div>
      {/* This implementation is a little strange, but it lets us swap SVG's responsively. */}
      <picture className={styles.urgent_message_popover_illustration} data-sentry-element="picture" data-sentry-source-file="UrgentMessagePopover.tsx">
        <source media="(min-width: 768px)" srcSet="/svg/woman-with-plant.svg" />
        <img alt="" src="/svg/exploring_nature.svg" loading="lazy" aria-hidden="true" />
      </picture>
    </div>;
};
export default UrgentMessagePopover;